// @flow
import jsonata from 'jsonata';
import type {
  Condition,
  ConditionGroup,
  FormData,
} from '../logic-builder-types';

async function evaluateCondition(
  condition: Condition,
  formData: FormData,
): Promise<boolean> {
  const { expression } = condition;

  // Check if expression is defined
  if (expression === undefined || expression === null) {
    throw new Error('Error evaluating condition: Expression field is missing');
  }

  let result;

  try {
    const jsonataExpression = jsonata(expression);
    result = await jsonataExpression.evaluate(formData);
  } catch (error) {
    throw new Error(`Error evaluating JSONata expression: ${error.message}`);
  }

  if (typeof result !== 'boolean') {
    if (result === undefined) {
      return false; // handle undefined results
    }
    throw new Error(
      `Unexpected result type: ${typeof result}. Expected a boolean value.`,
    );
  }

  return result;
}

async function evaluateConditionGroup(
  conditionGroup: ConditionGroup,
  formData: FormData,
): Promise<boolean> {
  const { matchConditions, conditions } = conditionGroup;

  if (matchConditions === 'all') {
    const results = await Promise.all(
      conditions.map(condition => evaluateCondition(condition, formData)),
    );
    return results.every(Boolean);
  }
  if (matchConditions === 'any') {
    const results = await Promise.all(
      conditions.map(condition => evaluateCondition(condition, formData)),
    );
    return results.some(Boolean);
  }

  throw new Error(`Unknown match conditions: ${matchConditions}`);
}

export { evaluateCondition, evaluateConditionGroup };
